/**
 * The runs page keeps the launch-agent dialog's open state entirely in the
 * react-router URL (`?new=true`, plus an optional `?draft=<id>` and
 * `?group=<agp_id>`), read straight from `location.search`.
 *
 * We deliberately do NOT mirror this through nuqs. nuqs writes the URL on a
 * throttle, so a stale writer can flush `?new=true` back onto the URL a tick
 * after a launch has already navigated into the new agent — reopening the
 * dialog "for no reason". Reading and writing the same react-router location
 * makes the open state a pure, synchronous function of the URL: there is no
 * second source of truth to lag, race, or resurrect the param. Once a launch
 * navigates to a URL without `new=true`, the dialog is closed and nothing can
 * flip it back open.
 */

/** Whether the runs page should show the launch-agent dialog. */
export function isLaunchDialogRequested(search: string): boolean {
  return new URLSearchParams(search).get('new') === 'true';
}

/** The draft id the launch dialog should preload, or null when none is set. */
export function launchDialogDraftId(search: string): string | null {
  return new URLSearchParams(search).get('draft');
}

/**
 * The group (`agp_*`) the dialog should file the launch into, or null when the
 * URL names none. Set by the sidebar's per-group "+" so a run started from a
 * group heading lands in that group.
 */
export function launchDialogGroupId(search: string): string | null {
  return new URLSearchParams(search).get('group');
}

/**
 * Add the launch-dialog query params, preserving other params.
 *
 * `draftId` and `groupId` are both tri-state: omit to leave any existing value
 * untouched, pass a value to set it, or pass `null` to clear it.
 */
export function withLaunchDialogQuery(
  search: string,
  draftId?: string | null,
  groupId?: string | null,
): string {
  const params = new URLSearchParams(search);
  params.set('new', 'true');
  setOrClear(params, 'draft', draftId);
  setOrClear(params, 'group', groupId);
  // `new=true` is always present, so the query string is never empty.
  return `?${params.toString()}`;
}

function setOrClear(params: URLSearchParams, key: string, value: string | null | undefined): void {
  if (value === undefined) return;
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

/** Remove launch-dialog query params, preserving other params. */
export function stripLaunchDialogQuery(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('new');
  params.delete('draft');
  params.delete('group');
  const next = params.toString();
  return next ? `?${next}` : '';
}
